import { __assign, __read, __spreadArray, __values } from "tslib";
import { useSingletonEdgeworthTags } from '@office-start/edgeworth-service';
import { useHarmonyItemTrie } from '@office-start/harmony-item-trie-service';
import { useDebugPerformanceLogger } from '@office-start/harmony-item-utilities';
import React, { createContext, Suspense, useContext, useEffect, useMemo, useState, } from 'react';
import { DataSourceInfo, } from './data-source-managers/managers';
export var InitialHarmonyItemDataContext = {
    items: [],
    queryItems: function () { return Promise.resolve([]); },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    mutateItem: function () { },
    providerStates: {
        edgeworth: 'unset',
        'recommended-files-api': 'unset',
        odsp: 'unset',
    },
};
var HarmonyItemDataContext = createContext(InitialHarmonyItemDataContext);
export var useHarmonyItemContext = function () {
    return useContext(HarmonyItemDataContext);
};
export var InitialHarmonyItemDataFetcherContext = {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    fetchItems: function () { },
};
var HarmonyItemDataFetcherContext = createContext(InitialHarmonyItemDataFetcherContext);
export var useHarmonyItemDataFetcherContext = function () {
    return useContext(HarmonyItemDataFetcherContext);
};
// TODO: Make generic and determine if needed or can be replaced with something else
export var intersectingDataTypes = function (a, b) {
    if (a.size < b.size) {
        return intersectingDataTypes(b, a);
    }
    var values = __spreadArray([], __read(a), false);
    return values.some(function (dataType) { return b.has(dataType); });
};
export var HarmonyItemDataProvider = function (_a) {
    var children = _a.children;
    // Set of data sources that children of the provider have requested
    var _b = __read(useState(new Set()), 2), activeSources = _b[0], setActiveSources = _b[1];
    // Set of data types that children of the provider have requested
    var _c = __read(useState(new Set()), 2), activeDataTypes = _c[0], setActiveDataTypes = _c[1];
    // Map to track data we have fetched so far
    var _d = __read(useState({}), 2), sourceData = _d[0], setSourceData = _d[1];
    // Get the state of each Data Source
    var _e = __read(useState({}), 2), providerStates = _e[0], setProviderStates = _e[1];
    var debugPerfLogger = useDebugPerformanceLogger();
    var _f = useHarmonyItemTrie(), addToTrie = _f.addToTrie, queryTrie = _f.queryTrie;
    var tags = useSingletonEdgeworthTags();
    // Function to update the state of the source in the sourceDataState record
    var updateProviderStates = useMemo(function () { return function (dataSource, status) {
        setProviderStates(function (current) {
            var _a;
            return __assign(__assign({}, current), (_a = {}, _a[dataSource] = status, _a));
        });
    }; }, []);
    // Function to update the sourceData record
    var updateSourceData = useMemo(function () { return function (dataSource, data) {
        setSourceData(function (current) {
            var _a;
            return __assign(__assign({}, current), (_a = {}, _a[dataSource] = data, _a));
        });
    }; }, []);
    // Merge the data fetched from the active sources
    var items = useMemo(function () {
        var e_1, _a, e_2, _b;
        var _c;
        var debugPerfLogEnd = debugPerfLogger.start({
            action: 'Data Service Merge',
            numSources: Object.keys(sourceData).length,
        });
        var outputMap = {};
        // Dedupe items
        var currentDocumentTagIds = [];
        try {
            for (var _d = __values(Object.keys(sourceData)), _e = _d.next(); !_e.done; _e = _d.next()) {
                var source = _e.value;
                var data = sourceData[source];
                try {
                    for (var data_1 = (e_2 = void 0, __values(data)), data_1_1 = data_1.next(); !data_1_1.done; data_1_1 = data_1.next()) {
                        var dataItem = data_1_1.value;
                        // Check if this document has any tags
                        if (tags && tags.docIdToTagIdsMap.has(dataItem.id)) {
                            currentDocumentTagIds = Array.from(
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            tags.docIdToTagIdsMap.get(dataItem.id));
                        }
                        else {
                            currentDocumentTagIds = [];
                        }
                        var current = (_c = outputMap[dataItem.id]) !== null && _c !== void 0 ? _c : { source: [] };
                        // TODO: Add datetime for merging
                        outputMap[dataItem.id] = __assign(__assign(__assign({}, current), dataItem), { source: current.source.concat(dataItem.source), tagIds: currentDocumentTagIds });
                    }
                }
                catch (e_2_1) { e_2 = { error: e_2_1 }; }
                finally {
                    try {
                        if (data_1_1 && !data_1_1.done && (_b = data_1.return)) _b.call(data_1);
                    }
                    finally { if (e_2) throw e_2.error; }
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_e && !_e.done && (_a = _d.return)) _a.call(_d);
            }
            finally { if (e_1) throw e_1.error; }
        }
        var values = Object.values(outputMap);
        debugPerfLogEnd({
            size: values.length,
        });
        return values;
    }, [debugPerfLogger, sourceData, tags]);
    // Add the items to the trie
    useEffect(function () {
        if (items.length) {
            addToTrie(items);
        }
    }, [items, addToTrie]);
    // Generates "dummy components" that fetch data from the data sources
    // It allows us to lazy load hooks that fetch data
    var dataSources = useMemo(function () {
        var configData = Object.entries(DataSourceInfo);
        return configData.map(function (_a) {
            var _b = __read(_a, 2), source = _b[0], Config = _b[1];
            if (activeSources.has(source) ||
                intersectingDataTypes(activeDataTypes, Config.dataTypes)) {
                return (
                // $TODO: Add a fallback component and log if this fails
                React.createElement(Suspense, { fallback: null, key: source },
                    React.createElement(Config.LazyManager, { key: source, onDataChange: function (data) {
                            return updateSourceData(source, data);
                        }, onStatusChange: function (status) {
                            return updateProviderStates(source, status);
                        } })));
            }
            return null;
        });
    }, [
        updateSourceData,
        updateProviderStates,
        activeSources,
        activeDataTypes,
    ]);
    // Function that is passed to the children of the provider
    // to allow for them to request new sources and/or data types
    var fetchItems = useMemo(function () {
        return function (settings) {
            var _a = settings !== null && settings !== void 0 ? settings : {}, dataTypes = _a.dataTypes, sources = _a.sources;
            if (dataTypes) {
                setActiveDataTypes(function (current) {
                    var needsUpdate = dataTypes.some(function (dataType) { return !current.has(dataType); });
                    return needsUpdate
                        ? new Set(__spreadArray(__spreadArray([], __read(current), false), __read(dataTypes), false))
                        : current;
                });
            }
            if (sources) {
                setActiveSources(function (current) {
                    var needsUpdate = sources.some(function (source) { return !current.has(source); });
                    return needsUpdate
                        ? new Set(__spreadArray(__spreadArray([], __read(current), false), __read(sources), false))
                        : current;
                });
            }
        };
    }, []);
    var queryItems = useMemo(function () { return function (text) { return queryTrie(items, text); }; }, [queryTrie, items]);
    var mutateItem = useMemo(function () { return function (id, newItem) {
        var e_3, _a;
        var newSourceData = __assign({}, sourceData);
        try {
            for (var _b = __values(newItem.source), _c = _b.next(); !_c.done; _c = _b.next()) {
                var itemSource = _c.value;
                var sourceItems = sourceData[itemSource];
                var index = sourceItems.findIndex(function (_) { return _.id === id; });
                if (index >= 0) {
                    sourceItems[index] = newItem;
                    newSourceData[itemSource] = sourceItems;
                }
            }
        }
        catch (e_3_1) { e_3 = { error: e_3_1 }; }
        finally {
            try {
                if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
            }
            finally { if (e_3) throw e_3.error; }
        }
        setSourceData(newSourceData);
    }; }, [sourceData]);
    return (React.createElement(React.Fragment, null,
        React.createElement(HarmonyItemDataFetcherContext.Provider, { value: {
                fetchItems: fetchItems,
            } },
            React.createElement(HarmonyItemDataContext.Provider, { value: {
                    items: items,
                    queryItems: queryItems,
                    mutateItem: mutateItem,
                    providerStates: providerStates,
                } }, children)),
        dataSources));
};
//# sourceMappingURL=HarmonyItemDataProvider.js.map